import type { AssetManifest } from '@kenocyber/shared';
import { mapCatalogEntry } from '@kenocyber/shared';
import Phaser from 'phaser';
import { assetUrl, findAnimatedAsset } from './assets';

/**
 * **Na caça, o alienígena e o chefe saem do `mapIndex` da sessão** (V28I.BN).
 *
 * ## O defeito que este módulo fecha
 *
 * `HuntScene.preload()` lia `MAP_CATALOG[0]` e carregava as duas folhas do mapa
 * 1 sob as chaves fixas `mob` e `boss`. O fundo, a ambiência, a vida, a escala e
 * a progressão já acompanhavam o mapa em que a nave estava — só as duas
 * criaturas é que não. Um piloto que ganhasse o mapa 7 lutava contra `alien1` e
 * `boss1` num céu do mapa 7, com a vida do mapa 7.
 *
 * É o MESMO defeito que o gate V28I.BI corrigiu no mundo aberto, e a razão dele
 * está escrita no `preload` do mundo aberto: quando o `preload` corre não se
 * sabe o que há para desenhar, e o índice 1 era o único que se podia saber de
 * antemão. Na caça isso nunca foi verdade — `HuntSceneOptions.session` já traz
 * o `mapIndex` autoritativo antes do `preload` —, e depois da primeira sessão o
 * mapa só muda numa viagem, que passa por `adoptSession`.
 *
 * ## A relação é a canónica, e não há segunda
 *
 * `mapCatalogEntry(N).mobAssetId` e `.bossAssetId`, a mesma fonte que decide o
 * `mapIndex` mecânico, a vida e a escala. Um mapa que mudasse de arte mudaria
 * num sítio só. O manifesto geral responde pelos quarenta ficheiros, e este
 * módulo não sabe onde eles estão.
 *
 * ## Nenhum recuo silencioso para o mapa 1
 *
 * Um índice fora de 1–50 é recusado por `mapCatalogEntry`, que lança. Um índice
 * de 21 a 50 tem `assetId` nulo — arte reservada para depois do alfa — e é
 * recusado aqui, com o índice no texto. Quem chama apanha a recusa e diz-a por
 * `hunt:scene-error`. Desenhar `alien1` no lugar de `alien7` seria mentir sobre
 * que criatura está no ecrã, e é exactamente o que este gate veio corrigir.
 *
 * ## As chaves são de FAMÍLIA e de MAPA
 *
 * `hunt-mob-7`, `hunt-boss-7`. Nunca `mob` nem `boss`: uma chave sem índice é
 * uma chave que mente na viagem seguinte, e foi assim que o defeito viveu tanto
 * tempo. O prefixo `hunt-` mantém-nas separadas das do mundo aberto, para uma
 * cena nunca libertar a textura que a outra está a desenhar.
 */

/**
 * **O marcador de lugar de um corpo.**
 *
 * Um disco baço, e NÃO a arte de outro mapa. Ele vive o instante entre uma
 * viagem e a chegada da folha do destino, e o que se vê nesse instante tem de
 * ser legivelmente "ainda não carregou" em vez de uma criatura errada — que é a
 * regra inteira deste gate. O mesmo marcador que o mundo aberto já usa, e pela
 * mesma razão.
 *
 * Nunca é usado na primeira sessão: o `preload` já conhece o `mapIndex` e a
 * folha certa chega antes do primeiro quadro.
 */
export const HUNT_MAP_BODY_PENDING_TEXTURE = 'hunt-map-body-pending';

const PENDING_PX = 64;

/** Gera o marcador uma vez por cena. Idempotente, e nunca por quadro. */
export function ensureHuntMapBodyPendingTexture(scene: Phaser.Scene): void {
  if (scene.textures.exists(HUNT_MAP_BODY_PENDING_TEXTURE)) {
    return;
  }
  const graphics = scene.make.graphics({ x: 0, y: 0 }, false);
  graphics.fillStyle(0x2a3446, 0.85);
  graphics.fillCircle(PENDING_PX / 2, PENDING_PX / 2, PENDING_PX / 2 - 2);
  graphics.lineStyle(2, 0x5c6c86, 0.9);
  graphics.strokeCircle(PENDING_PX / 2, PENDING_PX / 2, PENDING_PX / 2 - 2);
  graphics.generateTexture(HUNT_MAP_BODY_PENDING_TEXTURE, PENDING_PX, PENDING_PX);
  graphics.destroy();
}

/** As duas famílias que um mapa tem. A chave e o `assetId` saem do mesmo sítio. */
export type HuntMapBodyKind = 'mob' | 'boss';

/** A chave da textura desta família neste mapa. Derivada, nunca escrita à mão. */
export function huntMapBodyTextureKey(kind: HuntMapBodyKind, mapIndex: number): string {
  return `hunt-${kind}-${mapIndex}`;
}

/** A chave da animação. A mesma derivação, com um sufixo. */
export function huntMapBodyAnimKey(kind: HuntMapBodyKind, mapIndex: number): string {
  return `${huntMapBodyTextureKey(kind, mapIndex)}-idle`;
}

/** O `assetId` do manifesto para esta família neste mapa, ou uma recusa. */
export function huntMapBodyAssetId(kind: HuntMapBodyKind, mapIndex: number): string {
  /* Lança para um índice fora de [1, 50]. A recusa é do catálogo, e é uma só. */
  const entry = mapCatalogEntry(mapIndex);
  const assetId = kind === 'mob' ? entry.mobAssetId : entry.bossAssetId;
  if (assetId === null) {
    throw new RangeError(
      `hunt map ${mapIndex} has no ${kind} art; it is reserved for after the closed alpha`,
    );
  }
  return assetId;
}

export interface RequestHuntMapBodyInput {
  readonly scene: Phaser.Scene;
  readonly manifest: AssetManifest;
  readonly kind: HuntMapBodyKind;
  readonly mapIndex: number;
  /** O conjunto de chaves em voo da cena. Uma folha pedida não é re-pedida. */
  readonly pending: Set<string>;
  /**
   * Se o carregador deve arrancar já.
   *
   * `false` dentro de `preload`, onde o Phaser arranca o carregador sozinho a
   * seguir e mandá-lo arrancar a meio da fila só antecipa trabalho. `true`
   * depois de a cena existir, que é o caso de uma viagem: aí não há mais
   * ninguém para o arrancar.
   */
  readonly startNow: boolean;
}

/**
 * **Pede a folha 3x3 desta família neste mapa, uma vez.**
 *
 * Idempotente: uma textura que já existe ou já foi pedida devolve sem fazer
 * nada, portanto chamar isto uma vez por quadro custa duas buscas.
 *
 * Lança quando o índice não tem arte, e quem chama decide o que dizer — este
 * módulo não escolhe outra imagem por ele.
 */
export function requestHuntMapBody(input: RequestHuntMapBodyInput): void {
  const key = huntMapBodyTextureKey(input.kind, input.mapIndex);
  if (input.scene.textures.exists(key) || input.pending.has(key)) {
    return;
  }
  const asset = findAnimatedAsset(input.manifest, huntMapBodyAssetId(input.kind, input.mapIndex));
  const animKey = huntMapBodyAnimKey(input.kind, input.mapIndex);
  input.pending.add(key);
  input.scene.load.spritesheet(key, assetUrl(input.manifest, asset), {
    frameWidth: asset.layout.frameWidth,
    frameHeight: asset.layout.frameHeight,
  });
  input.scene.load.once(`filecomplete-spritesheet-${key}`, () => {
    input.pending.delete(key);
    /*
     * **A animação nasce com a folha**, e não no `create` da cena: antes de o
     * ficheiro chegar não há quadros para numerar. A cadência vem dos metadados
     * do recurso, como manda a disciplina de assets — nunca de um número
     * escrito aqui, e nunca do nome do ficheiro.
     */
    if (input.scene.anims.exists(animKey)) {
      return;
    }
    input.scene.anims.create({
      key: animKey,
      frames: input.scene.anims.generateFrameNumbers(key, {
        start: 0,
        end: asset.layout.frameCount - 1,
      }),
      frameRate: asset.animations[0]?.frameRate ?? 8,
      repeat: -1,
    });
  });
  /*
   * A mesma rede que os cascos, o drone e o mundo aberto têm: se o ficheiro
   * falhar, o evento acima nunca chega e a chave ficaria pendente para sempre —
   * e a folha nunca mais seria pedida nem substituída.
   */
  input.scene.load.once(Phaser.Loader.Events.COMPLETE, () => input.pending.delete(key));
  if (input.startNow) {
    input.scene.load.start();
  }
}

/**
 * **Larga a folha de um mapa e a animação dela.**
 *
 * Chamada quando a caça deixa um mapa, e só depois de as vistas que a
 * desenhavam terem sido destruídas.
 *
 * A animação sai COM a textura, e é o ponto que uma libertação parcial
 * estragaria: uma animação registada sobre uma folha que já não existe faria
 * `anims.exists` responder que sim, e o próximo corpo tocaria quadros que não
 * têm imagem por trás.
 *
 * Uma chave ainda em voo não é largada — o carregador está a escrevê-la, e
 * removê-la a meio deixaria o `filecomplete` a criar uma animação órfã. Ela é
 * pedida de novo quando for precisa, e a devolução do `boolean` diz a quem
 * chama se alguma coisa foi mesmo libertada.
 */
export function releaseHuntMapBody(input: {
  readonly scene: Phaser.Scene;
  readonly kind: HuntMapBodyKind;
  readonly mapIndex: number;
  readonly pending: ReadonlySet<string>;
}): boolean {
  const key = huntMapBodyTextureKey(input.kind, input.mapIndex);
  if (input.pending.has(key) || !input.scene.textures.exists(key)) {
    return false;
  }
  const animKey = huntMapBodyAnimKey(input.kind, input.mapIndex);
  if (input.scene.anims.exists(animKey)) {
    input.scene.anims.remove(animKey);
  }
  input.scene.textures.remove(key);
  return true;
}
